import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface MetaStore {
  ascensions: number;
  carryCoins: number;
  highestTierRankReached: number;
  graveyard: GraveyardEntry[];
  loreCodex: Record<string, LoreEntry>;
  unlockedAchievements: string[];
  lastLoginDate: string;     // YYYY-MM-DD UTC
  loginStreak: number;
  buffs: PersistentBuffs;
  recordTier: (rank: number) => void;
  awardCoins: (n: number) => void;
  spendCoins: (n: number) => boolean;
  recordAscension: (entry: GraveyardEntry) => void;
  recordLore: (id: string, title: string, snippet?: string) => void;
  unlockAchievement: (id: string) => void;
  isAchievementUnlocked: (id: string) => boolean;
  registerLogin: () => { newDay: boolean; streak: number; bonusCoins: number };
  buyBuff: (id: BuffId) => boolean;
}

export type BuffId =
  | 'starting-cash-1k'      // +$1K starting cash. Stackable. Cost: 1 each.
  | 'starting-wife-rep-5'   // +5 starting wife rep. Stackable. Cost: 3 each.
  | 'starting-irs-heat-neg5' // -5 starting IRS heat. Stackable. Cost: 2 each.
  | 'starting-followers-50' // +50 starting followers. Stackable. Cost: 1 each.
  | 'skip-boot'             // Skip boot sequence. One-time. Cost: 10.
  | 'starting-cash-10k'     // +$10K starting cash. Stackable. Cost: 8 each.
  | 'starting-mom-rep-10'   // +10 starting mom rep. Stackable. Cost: 2 each.
  | 'starting-sanity-10'    // +10 starting sanity. Stackable. Cost: 2 each.
  | 'starting-broker-rep-10' // +10 starting broker rep. Stackable. Cost: 2 each.
  | 'starting-coin-bonus-1' // +1 carry coin every login. One-time. Cost: 50.

export interface PersistentBuffs {
  startingCashBonus: number;       // dollars
  startingWifeRepBonus: number;
  startingIrsHeatReduction: number;
  startingFollowersBonus: number;
  skipBoot: boolean;
  startingMomRepBonus: number;
  startingSanityBonus: number;
  startingBrokerRepBonus: number;
  passiveCoinBonus: boolean;
}

export const BUFF_DEFS: Record<BuffId, { label: string; cost: number; oneTime?: boolean }> = {
  'starting-cash-1k': { label: '+$1,000 starting cash', cost: 1 },
  'starting-wife-rep-5': { label: '+5 starting wife rep', cost: 3 },
  'starting-irs-heat-neg5': { label: '-5 starting IRS heat', cost: 2 },
  'starting-followers-50': { label: '+50 starting followers', cost: 1 },
  'skip-boot': { label: 'Skip boot sequence', cost: 10, oneTime: true },
  'starting-cash-10k': { label: '+$10,000 starting cash', cost: 8 },
  'starting-mom-rep-10': { label: '+10 starting mom rep', cost: 2 },
  'starting-sanity-10': { label: '+10 starting sanity', cost: 2 },
  'starting-broker-rep-10': { label: '+10 starting broker rep', cost: 2 },
  'starting-coin-bonus-1': { label: '+1 carry coin per login', cost: 50, oneTime: true },
};

export interface LoreEntry {
  id: string;
  title: string;
  snippet?: string;
  firstSeenMs: number;
}

export interface GraveyardEntry {
  characterId: string;
  ascendedAt: number;
  highestTierRank: number;
  causeOfDeath: string;       // we use "cause of retirement" for ascension; "true death" for the rare exhaustion case
  finalNetWorth: number;
  runDurationMs: number;
}

export const useMeta = create<MetaStore>()(
  immer((set, get) => ({
    ascensions: 0,
    carryCoins: 0,
    highestTierRankReached: 0,
    graveyard: [],
    loreCodex: {},
    unlockedAchievements: [],
    lastLoginDate: '',
    loginStreak: 0,
    buffs: {
      startingCashBonus: 0,
      startingWifeRepBonus: 0,
      startingIrsHeatReduction: 0,
      startingFollowersBonus: 0,
      skipBoot: false,
      startingMomRepBonus: 0,
      startingSanityBonus: 0,
      startingBrokerRepBonus: 0,
      passiveCoinBonus: false,
    },
    recordTier: (rank) => set((s) => {
      if (rank > s.highestTierRankReached) s.highestTierRankReached = rank;
    }),
    recordLore: (id, title, snippet) => set((s) => {
      if (s.loreCodex[id]) return;
      s.loreCodex[id] = { id, title, snippet, firstSeenMs: Date.now() };
    }),
    awardCoins: (n) => set((s) => { s.carryCoins += n; }),
    spendCoins: (n) => {
      if (get().carryCoins < n) return false;
      set((s) => { s.carryCoins -= n; });
      return true;
    },
    recordAscension: (entry) => set((s) => {
      s.ascensions += 1;
      s.graveyard.push(entry);
      if (s.graveyard.length > 50) s.graveyard.shift();
    }),
    unlockAchievement: (id) => set((s) => {
      if (!s.unlockedAchievements.includes(id)) s.unlockedAchievements.push(id);
    }),
    isAchievementUnlocked: (id) => get().unlockedAchievements.includes(id),
    buyBuff: (id) => {
      const def = BUFF_DEFS[id];
      const s = get();
      if (def.oneTime) {
        if (id === 'skip-boot' && s.buffs.skipBoot) return false;
        if (id === 'starting-coin-bonus-1' && s.buffs.passiveCoinBonus) return false;
      }
      if (s.carryCoins < def.cost) return false;
      set((draft) => {
        draft.carryCoins -= def.cost;
        switch (id) {
          case 'starting-cash-1k': draft.buffs.startingCashBonus += 1000; break;
          case 'starting-wife-rep-5': draft.buffs.startingWifeRepBonus += 5; break;
          case 'starting-irs-heat-neg5': draft.buffs.startingIrsHeatReduction += 5; break;
          case 'starting-followers-50': draft.buffs.startingFollowersBonus += 50; break;
          case 'skip-boot': draft.buffs.skipBoot = true; break;
          case 'starting-cash-10k': draft.buffs.startingCashBonus += 10_000; break;
          case 'starting-mom-rep-10': draft.buffs.startingMomRepBonus += 10; break;
          case 'starting-sanity-10': draft.buffs.startingSanityBonus += 10; break;
          case 'starting-broker-rep-10': draft.buffs.startingBrokerRepBonus += 10; break;
          case 'starting-coin-bonus-1': draft.buffs.passiveCoinBonus = true; break;
        }
      });
      return true;
    },
    registerLogin: () => {
      const today = utcDateString(new Date());
      const prev = get().lastLoginDate;
      if (prev === today) return { newDay: false, streak: get().loginStreak, bonusCoins: 0 };

      let newStreak = 1;
      if (prev) {
        const yest = utcDateString(new Date(Date.now() - 86_400_000));
        if (prev === yest) newStreak = get().loginStreak + 1;
      }
      // Bonus coins on streak milestones (3, 7, 14, 30, 100 days)
      let bonus = 0;
      if ([3, 7, 14, 30, 100].includes(newStreak)) bonus = newStreak;
      // Passive coin buff: +1 coin on every login
      if (get().buffs.passiveCoinBonus) bonus += 1;

      set((s) => {
        s.lastLoginDate = today;
        s.loginStreak = newStreak;
        if (bonus > 0) s.carryCoins += bonus;
      });
      return { newDay: true, streak: newStreak, bonusCoins: bonus };
    },
  })),
);

function utcDateString(d: Date): string {
  return `${d.getUTCFullYear()}-${(d.getUTCMonth() + 1).toString().padStart(2, '0')}-${d.getUTCDate().toString().padStart(2, '0')}`;
}
